import { useEffect } from 'react'
import { posthog } from '../lib/posthog'

// The Conversations API isn't in posthog-js's public types yet, and the module
// is still lazy-loaded from the CDN with its own auto-show, so we enforce the
// desired visibility on an interval instead of calling show/hide once.
interface ConversationsApi {
  show: () => void
  hide: () => void
  isVisible: () => boolean
}

function getConversations(): ConversationsApi | undefined {
  return (
    posthog as unknown as { conversations?: ConversationsApi }
  ).conversations
}

/**
 * Root <html> class that allows the PostHog Conversations widget container
 * (#ph-conversations-widget-container) to render. index.css hides the
 * container whenever this class is absent, so the bubble can never leak onto
 * other screens even if the SDK re-renders itself between our hide() calls
 * (its render is async: it fetches tickets first, then mounts the DOM).
 */
export const CONVERSATIONS_VISIBLE_CLASS = 'ph-conversations-visible'

/**
 * Keeps the PostHog Conversations chat bubble visible only while `visible`
 * is true (i.e. on the Help & Support screen).
 */
export function useConversationsWidget(visible: boolean) {
  useEffect(() => {
    document.documentElement.classList.toggle(
      CONVERSATIONS_VISIBLE_CLASS,
      visible,
    )

    const enforce = () => {
      const conversations = getConversations()
      if (!conversations) return
      if (visible) {
        if (!conversations.isVisible()) conversations.show()
      } else {
        // Unconditional: hide() also cancels an in-flight auto-render, and
        // isVisible() is false while that render is still pending.
        conversations.hide()
      }
    }

    enforce()
    const id = setInterval(enforce, 1000)
    return () => {
      clearInterval(id)
      document.documentElement.classList.remove(CONVERSATIONS_VISIBLE_CLASS)
    }
  }, [visible])
}
